import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { User, Users, Network } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/components/ui/Dialog';
import type { Signal } from '@momentum/types';
import { SignalApplicabilityScope } from '@momentum/types';
import { useActivateSignal } from '@/shared/hooks/useSignals';

interface ActivateScopeDialogProps {
  signal: Signal | null;
  onClose: () => void;
  directReportsCount?: number;
  fullHierarchyCount?: number;
}

export const ActivateScopeDialog = ({
  signal,
  onClose,
  directReportsCount,
  fullHierarchyCount,
}: ActivateScopeDialogProps) => {
  const { t } = useTranslation('knowledge');
  const activateMutation = useActivateSignal();
  const [selected, setSelected] = useState(SignalApplicabilityScope.SELF);

  const open = !!signal;

  useEffect(() => {
    if (open) {
      setSelected(SignalApplicabilityScope.SELF);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!signal) return;

    try {
      await activateMutation.mutateAsync({
        id: signal._id,
        scope: selected,
      });
      onClose();
    } catch {
      toast.error(t('activateSignalError'));
    }
  };

  const options = [
    {
      value: SignalApplicabilityScope.SELF,
      icon: User,
      label: t('scopeJustMe'),
      description: t('scopeJustMeDesc'),
      iconBg: 'bg-[#eff6ff]',
      iconColor: 'text-[#2563eb]',
      selectedBorder: 'border-[#2563eb]/20 bg-[#2563eb]/[0.07]',
      radioBorder: 'border-[#2563eb]',
      radioText: 'text-[#2563eb]',
    },
    {
      value: SignalApplicabilityScope.DIRECT_REPORTS,
      icon: Users,
      label: t('scopeDirectReportsActivate'),
      description: t('scopeDirectReportsActivateDesc'),
      count: directReportsCount,
      iconBg: 'bg-[#faf5ff]',
      iconColor: 'text-[#9333ea]',
      selectedBorder: 'border-[#9333ea]/20 bg-[#9333ea]/[0.07]',
      radioBorder: 'border-[#9333ea]',
      radioText: 'text-[#9333ea]',
    },
    {
      value: SignalApplicabilityScope.FULL_HIERARCHY,
      icon: Network,
      label: t('scopeFullHierarchyActivate'),
      description: t('scopeFullHierarchyActivateDesc'),
      count: fullHierarchyCount,
      iconBg: 'bg-[#fef3c7]',
      iconColor: 'text-[#d97706]',
      selectedBorder: 'border-[#d97706]/20 bg-[#d97706]/[0.07]',
      radioBorder: 'border-[#d97706]',
      radioText: 'text-[#d97706]',
    },
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{t('activateDialogTitle')}</DialogTitle>
          <DialogDescription>
            {t('activateDialogDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2 py-2">
          {options.map((option) => {
            const Icon = option.icon;
            const isSelected = selected === option.value;
            return (
              <button
                key={option.value}
                type="button"
                className={`flex cursor-pointer items-center gap-3 rounded-lg border p-2.5 ps-3.5 text-start transition-all ${
                  isSelected
                    ? option.selectedBorder
                    : 'border-[#f0ece6] bg-transparent hover:bg-black/[0.015]'
                }`}
                onClick={() => setSelected(option.value)}
              >
                {/* Radio dot */}
                <div
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-[1.5px] transition-all ${
                    isSelected
                      ? `${option.radioBorder} ${option.radioText}`
                      : 'border-[#ece8e1]'
                  }`}
                >
                  <div
                    className={`h-2 w-2 rounded-full transition-all ${
                      isSelected ? 'bg-current' : 'bg-transparent'
                    }`}
                  />
                </div>
                {/* Icon */}
                <div
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${option.iconBg} ${option.iconColor}`}
                >
                  <Icon className="h-3.5 w-3.5" />
                </div>
                <div className="flex flex-col gap-px">
                  <span className="flex items-center gap-1.5 text-sm font-medium text-[#2c2520]">
                    {option.label}
                    {'count' in option && option.count != null && (
                      <span className="text-[11px] font-normal text-muted-foreground">
                        ({option.count} + {t('scopeCountYou')})
                      </span>
                    )}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    {option.description}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={activateMutation.isPending}
          >
            {t('createSignalCancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={activateMutation.isPending}
            className="bg-[#7c6bc4] hover:bg-[#6a5aae]"
          >
            {activateMutation.isPending
              ? t('activateDialogActivating')
              : t('activateDialogConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
